/**
 * Custom overrides for BilligKoste
 *
 *  - Force all Shopify money formatting to use Danish locale rules (dot for thousands, comma for decimals)
 *  - Re-apply formatting when theme JS updates price DOM nodes
 */

(function () {
  const PRICE_SELECTOR =
      ".price.price--highlight, .price.price--compare, .vat-price, .line-item__price.line-item__price--highlight, .line-item__price.line-item__price--compare, .cart-recap__price-line-price";
  const DATA_CENTS_ATTRIBUTE = "data-billigkoste-money-cents";
  const DATA_INC_CENTS_ATTRIBUTE = "data-billigkoste-inc-cents";
  const VAT_RATE = 1.25;

  const isExcVat = () => document.body.classList.contains("exc-vat");

  const normaliseCents = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return Math.round(value);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") {
        return null;
      }

      // First try plain integer (common in data attributes)
      if (/^-?\d+$/.test(trimmed)) {
        return parseInt(trimmed, 10);
      }

      // Fall back to parsing textual prices such as "1.234,56 DKK"
      const normalised = trimmed
          .replace(/[^0-9,.-]/g, "") // keep digits, decimal separators and sign
          .replace(/\.(?=\d{3}(\D|$))/g, "") // strip thousand dots
          .replace(",", "."); // convert decimal comma to dot

      const parsedFloat = parseFloat(normalised);
      if (Number.isNaN(parsedFloat)) {
        return null;
      }

      return Math.round(parsedFloat * 100);
    }

    return null;
  };

  const formatCents = (cents) => {
    let intCents = normaliseCents(cents);

    if (intCents === null) {
      return "";
    }

    const isNegative = intCents < 0;
    intCents = Math.abs(intCents);

    const whole = Math.floor(intCents / 100);
    const fraction = intCents % 100;

    const integerPart = whole
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = fraction.toString().padStart(2, "0");

    return `${isNegative ? "-" : ""}kr. ${integerPart},${decimalPart}`;
  };

  const extractCentsFromNode = (node) => {
    const candidateAttributes = [
      "data-money",
      "data-money-raw",
      "data-price",
      "data-price-raw",
      "data-value",
      "data-amount",
      "data-total",
    ];

    for (const attribute of candidateAttributes) {
      if (node.hasAttribute(attribute)) {
        const attributeValue = node.getAttribute(attribute);
        const cents = normaliseCents(attributeValue);
        if (cents !== null) {
          return cents;
        }
      }
    }

    const datasetKeys = [
      "money",
      "moneyRaw",
      "price",
      "priceRaw",
      "value",
      "amount",
      "total",
    ];

    for (const key of datasetKeys) {
      if (Object.prototype.hasOwnProperty.call(node.dataset, key)) {
        const datasetValue = node.dataset[key];
        const cents = normaliseCents(datasetValue);
        if (cents !== null) {
          return cents;
        }
      }
    }

    return normaliseCents(node.textContent);
  };

  const applyFormattedMoney = (node) => {
    // Use stored inkl. moms cents if available, otherwise extract fresh
    let incCents;
    if (node.hasAttribute(DATA_INC_CENTS_ATTRIBUTE)) {
      incCents = parseInt(node.getAttribute(DATA_INC_CENTS_ATTRIBUTE), 10);
    } else {
      incCents = extractCentsFromNode(node);
      if (incCents === null) {
        return;
      }
      // Store original inkl. moms cents (immutable source of truth)
      node.setAttribute(DATA_INC_CENTS_ATTRIBUTE, incCents.toString());
    }

    const displayCents = isExcVat()
        ? Math.round(incCents / VAT_RATE)
        : incCents;

    node.setAttribute(DATA_CENTS_ATTRIBUTE, displayCents.toString());
    node.textContent = formatCents(displayCents);
  };

  const reformatPrices = (root = document) => {
    root.querySelectorAll(PRICE_SELECTOR).forEach((node) => applyFormattedMoney(node));
  };

  // Ensure product page / cart prices only show a single tax helper, placed
  // directly below the "current" price (highlight price where available).
  const normalizeMainPriceHelpers = (root = document) => {
    const priceLists = root.querySelectorAll(".product-meta__price-list, .price-list");

    priceLists.forEach((list) => {
      const highlightPrice = list.querySelector(".price.price--highlight");
      const comparePrice = list.querySelector(".price.price--compare");

      // Remove any existing helpers in this block so we can rebuild a single one.
      list.querySelectorAll(".tax-price-helper").forEach((el) => el.remove());

      const currentPrice = highlightPrice || comparePrice;
      if (!currentPrice) return;

      // Wrap the current price and the helper in a dedicated wrapper so the
      // helper is always stacked below the price.
      let wrapper = currentPrice.closest(".tax-price-wrapper");
      if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.className = "tax-price-wrapper";
        currentPrice.parentElement.insertBefore(wrapper, currentPrice);
        wrapper.appendChild(currentPrice);
      }

      const helper = document.createElement("div");
      helper.className = "tax-price-helper";
      helper.textContent = isExcVat() ? "(ekskl. moms)" : "(inkl. moms)";
      wrapper.appendChild(helper);
    });
  };

  // App bundle (Avada) occasionally injects multiple tax labels inside its own
  // `.Avada-Offer__Price` blocks. Keep at most one helper per Avada block.
  const normalizeTaxHelpers = (root = document) => {
    const priceBlocks = root.querySelectorAll(".Avada-Offer__Price");
    priceBlocks.forEach((priceBlock) => {
      // Remove all existing helpers so we can rebuild a clean, single helper.
      priceBlock.querySelectorAll(".tax-price-helper").forEach((el) => el.remove());

      // In case the app injects its own duplicate "(inkl. moms)" nodes with a different class,
      // aggressively remove any simple element whose sole text content matches that label.
      priceBlock.querySelectorAll("*").forEach((el) => {
        if (
          el.childNodes.length === 1 &&
          typeof el.textContent === "string" &&
          el.textContent.trim().toLowerCase() === "(inkl. moms)"
        ) {
          el.remove();
        }
      });
    });
  };

  // Low-latency helper normalization: run immediately, then coalesce a follow-up in rAF.
  const normalizeTaxHelpersThrottled = (() => {
    let scheduled = false;
    return (root = document) => {
      // run immediately to minimize visible lag
      normalizeTaxHelpers(root);
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        normalizeTaxHelpers(root);
      });
    };
  })();

  const applyCustomFormatting = (root = document) => {
    reformatPrices(root);
    normalizeMainPriceHelpers(root);
    normalizeTaxHelpers(root);
  };

  // Override Shopify.formatMoney so future theme invocations use our formatter.
  window.Shopify = window.Shopify || {};
  const originalFormatMoney = window.Shopify.formatMoney;

  window.Shopify.formatMoney = function (money, format) {
    const cents = normaliseCents(money);
    const formatted = formatCents(cents);
    return formatted || (originalFormatMoney ? originalFormatMoney(money, format) : "");
  };

  // Initial format after DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => applyCustomFormatting());
  } else {
    applyCustomFormatting();
  }

  // Observe DOM mutations to keep formatting in sync
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.matches && node.matches(PRICE_SELECTOR)) {
          applyFormattedMoney(node);
          normalizeTaxHelpers(node.parentElement || node);
        } else if (node.querySelector) {
          applyCustomFormatting(node);
        }
      });

      // Also sanitize the mutation target subtree in case the app rewrites innerHTML
      // without adding top-level nodes we explicitly catch above.
      if (mutation.target instanceof HTMLElement) {
        normalizeTaxHelpersThrottled(mutation.target);
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Re-apply formatting after known theme events
  const watchedEvents = [
    "product:added",
    "variant:changed",
    "cart:refresh",
    "cart:updated",
    "theme:loading:end",
    "shopify:section:load",
    "shopify:section:reorder",
    "shopify:section:select",
    "shopify:section:deselect",
    "shopify:block:select",
    "shopify:block:deselect",
  ];

  watchedEvents.forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event && event.target instanceof HTMLElement) {
        applyCustomFormatting(event.target);
      } else {
        applyCustomFormatting();
      }
    });
  });

  // Also normalize after Avada’s own custom events if present.
  document.addEventListener("aov:bundle:updated", () => applyCustomFormatting());
  document.addEventListener("aov:bundle:selected", () => applyCustomFormatting());

  // Final safety: periodic cleanup in case app mutations slip past observers.
  setInterval(() => normalizeTaxHelpersThrottled(document), 200);

  // ---------------------------------------------------------------------------
  // OMG "Flerstykspriser" quantity-breaks table VAT handling
  //
  // The OMG app always renders prices incl. VAT inside:
  //   .groupsTable .omg-table td span.money
  //
  // We store the original incl. VAT cents in data-billigkoste-inc-cents and
  // then either show the raw incl. value or divide by VAT_RATE for excl.
  // ---------------------------------------------------------------------------
  // Format cents in the OMG app's own style: "230,07 DKK"
  const formatCentsOmg = (cents) => {
    const isNegative = cents < 0;
    const absCents = Math.abs(cents);
    const whole = Math.floor(absCents / 100);
    const fraction = absCents % 100;
    const integerPart = whole
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = fraction.toString().padStart(2, "0");
    return `${isNegative ? "-" : ""}${integerPart},${decimalPart} DKK`;
  };

  // Helper: get the first direct text node of an element (ignoring child spans).
  const getFirstTextNode = (el) => {
    for (const child of el.childNodes) {
      if (child.nodeType === Node.TEXT_NODE && child.textContent.trim() !== "") {
        return child;
      }
    }
    return null;
  };

  const applyOmgMoneySpan = (span) => {
    // First call: parse and store the original incl. VAT value from the app.
    let incCents;
    if (span.hasAttribute(DATA_INC_CENTS_ATTRIBUTE)) {
      incCents = parseInt(span.getAttribute(DATA_INC_CENTS_ATTRIBUTE), 10);
    } else {
      // Read only the first text node to avoid picking up child element text
      // (e.g. Snize's <span class="snize-highlight-saving-text">).
      const textNode = getFirstTextNode(span);
      const rawText = textNode ? textNode.textContent : span.textContent;
      incCents = normaliseCents(rawText);
      if (incCents === null) return;
      span.setAttribute(DATA_INC_CENTS_ATTRIBUTE, incCents.toString());
    }

    const displayCents = isExcVat()
      ? Math.round(incCents / VAT_RATE)
      : incCents;

    const formatted = formatCentsOmg(displayCents);

    // Update only the text node to preserve child elements (e.g. snize-highlight-saving-text).
    const textNode = getFirstTextNode(span);
    if (textNode) {
      textNode.textContent = formatted;
    } else {
      // No text node yet — prepend one before any child elements.
      span.insertBefore(document.createTextNode(formatted), span.firstChild);
    }
  };

  const applyOmgTablePrices = (root = document) => {
    root
      .querySelectorAll(".groupsTable .omg-table td span.money")
      .forEach((span) => applyOmgMoneySpan(span));
  };

  // Update the "Flerstykspriser (ekskl./inkl. moms)" heading inside .groupsTable.
  const updateOmgTableTitle = (root = document) => {
    root.querySelectorAll(".groupsTable .omg-order-discount-name").forEach((el) => {
      if (isExcVat()) {
        el.innerHTML = el.innerHTML.replace(/\(inkl\. moms\)/gi, "(ekskl. moms)");
      } else {
        el.innerHTML = el.innerHTML.replace(/\(ekskl\. moms\)/gi, "(inkl. moms)");
      }
    });
  };

  const applyOmgFormatting = (root = document) => {
    applyOmgTablePrices(root);
    updateOmgTableTitle(root);
  };

  // Watch for the OMG app injecting its table. We use a separate observer
  // that only fires applyOmgFormatting when new nodes are added inside
  // .groupsTable containers. A processing flag prevents infinite loops
  // (since applyOmgFormatting itself modifies textContent).
  let omgProcessing = false;
  const omgObserver = new MutationObserver((mutations) => {
    if (omgProcessing) return;
    // Only act if a mutation actually occurred inside a .groupsTable
    const relevant = mutations.some((m) =>
      m.target.closest && m.target.closest(".groupsTable")
    );
    if (!relevant) return;
    omgProcessing = true;
    requestAnimationFrame(() => {
      applyOmgFormatting();
      omgProcessing = false;
    });
  });
  // Only observe .groupsTable containers when they exist
  const startOmgObserver = () => {
    const tables = document.querySelectorAll(".groupsTable");
    if (tables.length) {
      tables.forEach((el) => {
        omgObserver.observe(el, { childList: true, subtree: true, characterData: true });
      });
    } else {
      // App hasn't loaded yet — wait for .groupsTable to appear, then init
      const waitObserver = new MutationObserver(() => {
        const t = document.querySelectorAll(".groupsTable");
        if (t.length) {
          waitObserver.disconnect();
          applyOmgFormatting();
          t.forEach((el) => {
            omgObserver.observe(el, { childList: true, subtree: true, characterData: true });
          });
        }
      });
      waitObserver.observe(document.body, { childList: true, subtree: true });
    }
  };

  // ---------------------------------------------------------------------------
  // Snize search app VAT handling
  //
  // Search results are injected dynamically when the user types. Prices live in:
  //   .snize-price-list span.snize-price.money
  // Snize prices are INKL. moms by default (like theme prices), so we divide
  // down for ekskl. display — opposite of OMG which starts ekskl.
  // ---------------------------------------------------------------------------
  const SNIZE_PRICE_SELECTOR = ".snize-price-list span.snize-price.money";

  const applySnizeMoneySpan = (span) => {
    let incCents;
    if (span.hasAttribute(DATA_INC_CENTS_ATTRIBUTE)) {
      incCents = parseInt(span.getAttribute(DATA_INC_CENTS_ATTRIBUTE), 10);
    } else {
      const textNode = getFirstTextNode(span);
      const rawText = textNode ? textNode.textContent : span.textContent;
      incCents = normaliseCents(rawText);
      if (incCents === null) return;
      span.setAttribute(DATA_INC_CENTS_ATTRIBUTE, incCents.toString());
    }

    const displayCents = isExcVat()
      ? Math.round(incCents / VAT_RATE)
      : incCents;

    const formatted = formatCentsOmg(displayCents);
    const textNode = getFirstTextNode(span);
    if (textNode) {
      textNode.textContent = formatted;
    } else {
      span.insertBefore(document.createTextNode(formatted), span.firstChild);
    }
  };

  const applySnizePrices = (root = document) => {
    root.querySelectorAll(SNIZE_PRICE_SELECTOR).forEach((span) => {
      applySnizeMoneySpan(span);
    });
  };

  // Search results appear/change dynamically — observe mutations on body.
  // The Snize autocomplete dropdown is appended directly to body (not inside
  // the search form), so we must observe body to catch it. Guard flag + rAF
  // ensure we process at most once per frame.
  let snizeProcessing = false;
  const snizeObserver = new MutationObserver(() => {
    if (snizeProcessing) return;
    // Quick bail-out: skip if no snize price elements exist in DOM right now.
    if (!document.querySelector(SNIZE_PRICE_SELECTOR)) return;
    snizeProcessing = true;
    requestAnimationFrame(() => {
      applySnizePrices();
      snizeProcessing = false;
    });
  });

  const startSnizeObserver = () => {
    snizeObserver.observe(document.body, { childList: true, subtree: true });
  };

  // ---------------------------------------------------------------------------
  // VAT toggle: recalculate all prices (theme + OMG + Snize) when switching
  // ---------------------------------------------------------------------------
  const recalculateVatPrices = () => {
    applyCustomFormatting();
    applyOmgFormatting();
    applySnizePrices();
  };

  // Sync toggle state on page load based on stored preference
  const initVatToggle = () => {
    const toggle = document.getElementById("vat-toggle");
    if (!toggle) return;

    try {
      const pref = localStorage.getItem("vatPreference");
      if (pref === "exc") {
        toggle.checked = false;
        // Body class is handled by VatSwitcherContainer in theme.js,
        // but ensure it's set before we recalculate prices
        if (!document.body.classList.contains("exc-vat")) {
          document.body.classList.remove("inc-vat");
          document.body.classList.add("exc-vat");
        }
        recalculateVatPrices();
      }
    } catch (e) {
      // localStorage not available
    }

    toggle.addEventListener("change", () => {
      recalculateVatPrices();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initVatToggle();
      applyOmgFormatting();
      startOmgObserver();
      startSnizeObserver();
    });
  } else {
    initVatToggle();
    applyOmgFormatting();
    startOmgObserver();
    startSnizeObserver();
  }
})();
